import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as adminService from '@/services/adminService';

const statusStyles = {
  working: { label: 'A trabalhar', dot: 'bg-forest-light', ring: 'shadow-[0_0_18px_rgba(76,175,80,0.45)]', bar: 'from-forest-light to-gold' },
  thinking: { label: 'A pensar', dot: 'bg-gem', ring: 'shadow-[0_0_18px_rgba(155,89,182,0.45)]', bar: 'from-gem to-ocean-light' },
  watching: { label: 'A vigiar', dot: 'bg-ocean-light', ring: 'shadow-[0_0_18px_rgba(58,143,209,0.45)]', bar: 'from-ocean-light to-xp' },
  alert: { label: 'Alerta', dot: 'bg-energy', ring: 'shadow-[0_0_20px_rgba(231,76,60,0.55)]', bar: 'from-energy to-pirate-red' },
  idle: { label: 'Em espera', dot: 'bg-fg/40', ring: 'shadow-none', bar: 'from-white/30 to-white/10' },
};

const summaryLabels = {
  working: 'Ativos',
  watching: 'A vigiar',
  alerts: 'Alertas',
  idle: 'Em espera',
};

// ─── Modal genérico ────────────────────────────────────────────────────────────
function Modal({ open, onClose, title, children }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            className="w-full max-w-lg rounded-xl border border-accent/20 bg-[#0f1117] p-6 shadow-2xl"
            initial={{ scale: 0.93, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.93, opacity: 0 }}
            transition={{ duration: 0.18 }}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-serif text-xl font-black text-accent">{title}</h3>
              <button onClick={onClose} className="text-fg/40 hover:text-fg/80 text-lg leading-none">✕</button>
            </div>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ─── Modal Criar com IA ────────────────────────────────────────────────────────
function AiCreateModal({ open, onClose, type }) {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [creating, setCreating] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const navigate = useNavigate();

  const isZone = type === 'zone';
  const placeholder = isZone
    ? 'Ex: Zona histórica do centro de Aveiro, junto aos canais, com arquitetura Art Nouveau...'
    : 'Ex: Café Museu de Aveiro, no centro histórico, especialidade em ovos moles, lat 40.6405 lng -8.6538...';

  const reset = () => { setText(''); setSuggestion(null); setDone(false); setErr(''); };

  const handleClose = () => { reset(); onClose(); };

  const suggest = async () => {
    setLoading(true); setErr(''); setSuggestion(null);
    try {
      const res = isZone
        ? await adminService.aiSuggestZone(text)
        : await adminService.aiSuggestPOI(text);
      setSuggestion(res.data.data);
    } catch (e) {
      setErr(e.response?.data?.message || 'Erro ao contactar a IA.');
    } finally {
      setLoading(false);
    }
  };

  const create = async () => {
    setCreating(true); setErr('');
    try {
      if (isZone) {
        await adminService.zones.create(suggestion);
      } else {
        await adminService.pois.create({ ...suggestion, isActive: true });
      }
      setDone(true);
    } catch (e) {
      setErr(e.response?.data?.message || 'Erro ao criar.');
    } finally {
      setCreating(false);
    }
  };

  const goTo = () => {
    handleClose();
    navigate(isZone ? '/admin/zones' : '/admin/pois');
  };

  return (
    <Modal open={open} onClose={handleClose} title={isZone ? 'Sugerir nova Zona com IA' : 'Sugerir novo POI com IA'}>
      {!done ? (
        <>
          <p className="mb-3 text-sm text-fg/50">
            Descreve em linguagem natural. A IA extrai os campos automaticamente.
          </p>
          <textarea
            rows={4}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded-lg border border-fg/15 bg-fg/5 p-3 text-sm text-fg placeholder-white/25 focus:border-accent/50 focus:outline-none resize-none"
          />
          {err && <p className="mt-2 text-xs text-energy">{err}</p>}

          {!suggestion && (
            <button
              onClick={suggest}
              disabled={loading || text.trim().length < 5}
              className="mt-3 w-full rounded-lg border border-accent/40 bg-accent/10 py-2 text-sm font-bold text-accent hover:bg-accent/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'A gerar sugestão...' : 'Gerar sugestão com IA'}
            </button>
          )}

          {suggestion && (
            <div className="mt-4">
              <div className="mb-2 text-xs font-bold uppercase tracking-widest text-accent/60">Sugestão gerada</div>
              <div className="rounded-lg border border-fg/10 bg-black/20 p-3 space-y-1.5">
                {Object.entries(suggestion).map(([k, v]) => (
                  v !== null && v !== undefined && (
                    <div key={k} className="flex gap-2 text-sm">
                      <span className="min-w-[110px] font-bold text-fg/40">{k}</span>
                      <span className="text-fg/80 break-all">{String(v)}</span>
                    </div>
                  )
                ))}
              </div>
              {err && <p className="mt-2 text-xs text-energy">{err}</p>}
              <div className="mt-4 flex gap-2">
                <button
                  onClick={() => setSuggestion(null)}
                  className="flex-1 rounded-lg border border-fg/15 py-2 text-sm text-fg/60 hover:bg-fg/5"
                >
                  Tentar novamente
                </button>
                <button
                  onClick={create}
                  disabled={creating}
                  className="flex-1 rounded-lg border border-forest-light/40 bg-forest-light/10 py-2 text-sm font-bold text-forest-light hover:bg-forest-light/20 disabled:opacity-50"
                >
                  {creating ? 'A criar...' : `Criar ${isZone ? 'zona' : 'POI'} agora`}
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-4">
          <div className="text-4xl mb-3">✅</div>
          <p className="text-fg font-bold text-lg">{isZone ? 'Zona criada' : 'POI criado'} com sucesso!</p>
          <p className="text-fg/50 text-sm mt-1">Podes editar os detalhes na página respetiva.</p>
          <div className="mt-5 flex gap-2 justify-center">
            <button onClick={handleClose} className="rounded-lg border border-fg/15 px-4 py-2 text-sm text-fg/60 hover:bg-fg/5">
              Fechar
            </button>
            <button onClick={goTo} className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-bold text-accent hover:bg-accent/20">
              Ver {isZone ? 'zonas' : 'POIs'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── Componentes de sugestão ───────────────────────────────────────────────────
function SuggestionColumn({ title, children }) {
  return (
    <div className="rounded-lg border border-fg/10 bg-black/15 p-3">
      <div className="text-xs font-bold uppercase tracking-[0.16em] text-accent/65">{title}</div>
      <div className="mt-3 grid gap-3">{children}</div>
    </div>
  );
}

function SuggestionCard({ title, meta, detail, action }) {
  return (
    <div className="rounded-lg border border-fg/10 bg-fg/[0.03] p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="font-bold text-fg leading-snug">{title}</div>
        {meta && <span className="shrink-0 text-xs text-fg/35">{meta}</span>}
      </div>
      <p className="mt-1 text-xs leading-relaxed text-fg/50">{detail || 'Sem explicação disponível.'}</p>
      {action && (
        <div className="mt-2">
          {action}
        </div>
      )}
    </div>
  );
}

// ─── Página principal ──────────────────────────────────────────────────────────
export default function AdminAgentsPage() {
  const [data, setData] = useState(null);
  const [aiLogs, setAiLogs] = useState([]);
  const [aiRun, setAiRun] = useState(null);
  const [runningAi, setRunningAi] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [aiModal, setAiModal] = useState(null); // 'zone' | 'poi' | null
  const [activatingMission, setActivatingMission] = useState(null);
  const navigate = useNavigate();

  const loadData = async (active = true) => {
    try {
      const [statusRes, logsRes] = await Promise.all([
        adminService.getAgentsStatus(),
        adminService.getAiBrainLogs(),
      ]);
      if (!active) return;
      const logs = logsRes.data.data?.logs || [];
      setData(statusRes.data.data);
      setAiLogs(logs);
      setAiRun(logs.find((log) => log.status === 'completed' && log.result)?.result || null);
      setError('');
    } catch {
      if (active) setError('Não foi possível carregar o estado dos agentes.');
    }
  };

  useEffect(() => {
    let active = true;
    loadData(active);
    const interval = setInterval(() => loadData(active), 15000);
    return () => { active = false; clearInterval(interval); };
  }, []);

  const runAiBrain = async () => {
    setRunningAi(true); setError(''); setNotice('');
    try {
      const res = await adminService.runAiBrainAgent();
      const log = res.data.data;
      setAiRun(log.result || null);
      setAiLogs((current) => [log, ...current.filter((item) => item.id !== log.id)].slice(0, 20));
      setNotice(log.message || 'AiBrainAgent executado com sucesso.');
      await loadData(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível executar o AiBrainAgent.');
    } finally {
      setRunningAi(false);
    }
  };

  const activateMission = async (missionId) => {
    setActivatingMission(missionId);
    try {
      await adminService.missions.update(missionId, { isActive: true });
      setNotice('Missão ativada com sucesso.');
    } catch {
      setError('Não foi possível ativar a missão.');
    } finally {
      setActivatingMission(null);
    }
  };

  const agents = data?.agents || [];
  const activity = data?.recentActivity || [];
  const suggestions = aiRun?.suggestions;

  return (
    <section className="min-h-full">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.2em] text-accent/70">Centro de automação</p>
          <h1 className="mt-1 font-serif text-3xl font-black text-accent">Agentes em tempo real</h1>
          <p className="mt-2 max-w-2xl text-sm text-fg/55">
            Visão operacional dos agentes do Hakken City, com tarefas atuais, sinais de trabalho e alertas do sistema.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={runAiBrain}
            disabled={runningAi}
            className="rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-bold text-accent hover:bg-accent/20 disabled:cursor-wait disabled:opacity-60"
          >
            {runningAi ? 'A executar...' : 'Executar agente'}
          </button>
          <div className="rounded-lg border border-accent/20 bg-fg/5 px-4 py-3 text-right">
            <div className="text-xs text-fg/40">Última leitura</div>
            <div className="font-mono text-sm text-accent">
              {data?.generatedAt ? new Date(data.generatedAt).toLocaleTimeString('pt-PT') : '--:--:--'}
            </div>
          </div>
        </div>
      </div>

      {(error || notice) && (
        <div className={`mt-5 rounded-lg border p-4 text-sm font-bold ${error ? 'border-energy/40 bg-energy/10 text-energy' : 'border-forest-light/40 bg-forest-light/10 text-forest-light'}`}>
          {error || notice}
        </div>
      )}

      {/* Summary cards */}
      <div className="mt-6 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {Object.entries(data?.summary || { working: 0, watching: 0, alerts: 0, idle: 0 }).map(([key, value]) => (
          <div key={key} className="rounded-lg border border-fg/10 bg-fg/[0.04] p-4">
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-fg/35">{summaryLabels[key]}</div>
            <div className="mt-2 font-serif text-3xl font-black text-fg">{value}</div>
          </div>
        ))}
      </div>

      {/* Agents grid + activity */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="grid gap-4 lg:col-span-2 xl:grid-cols-2">
          {agents.map((agent, index) => {
            const style = statusStyles[agent.status] || statusStyles.idle;
            return (
              <motion.article
                key={agent.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.04 }}
                className={`rounded-lg border border-accent/15 bg-gradient-to-br from-white/[0.08] to-white/[0.025] p-4 ${style.ring}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`h-2.5 w-2.5 rounded-full ${style.dot}`}>
                        {['working', 'thinking', 'alert'].includes(agent.status) && (
                          <span className={`block h-2.5 w-2.5 animate-ping rounded-full ${style.dot}`} />
                        )}
                      </span>
                      <h2 className="font-serif text-lg font-black text-accent">{agent.name}</h2>
                    </div>
                    <p className="mt-1 text-xs font-bold uppercase tracking-[0.16em] text-fg/35">{agent.role}</p>
                  </div>
                  <span className="rounded-full border border-fg/10 bg-black/20 px-3 py-1 text-xs font-bold text-fg/70">
                    {style.label}
                  </span>
                </div>
                <p className="mt-4 min-h-[42px] text-sm font-semibold leading-relaxed text-fg/78">{agent.currentTask}</p>
                <div className="mt-4">
                  <div className="mb-1 flex justify-between text-xs text-fg/45">
                    <span>Progresso operacional</span>
                    <span>{agent.progress}%</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-black/30">
                    <motion.div
                      className={`h-full rounded-full bg-gradient-to-r ${style.bar}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${agent.progress}%` }}
                      transition={{ duration: 0.7, ease: 'easeOut' }}
                    />
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-[auto_1fr] gap-3 rounded-lg border border-fg/10 bg-black/15 p-3">
                  <div>
                    <div className="font-serif text-2xl font-black text-fg">{agent.metricValue}</div>
                    <div className="text-[11px] font-bold uppercase text-fg/35">{agent.metricLabel}</div>
                  </div>
                  <p className="self-center text-sm text-fg/55">{agent.detail}</p>
                </div>
              </motion.article>
            );
          })}
        </div>

        <aside className="rounded-lg border border-accent/15 bg-fg/[0.04] p-4">
          <h2 className="font-serif text-xl font-black text-accent">Atividade recente</h2>
          <div className="mt-4 grid gap-3">
            {activity.map((item, index) => (
              <div key={`${item.agent}-${index}`} className="rounded-lg border border-fg/10 bg-black/15 p-3">
                <div className="text-xs font-bold uppercase tracking-[0.16em] text-accent/65">{item.agent}</div>
                <p className="mt-1 text-sm text-fg/70">{item.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 rounded-lg border border-ocean-light/25 bg-ocean-light/10 p-3">
            <div className="text-xs font-bold uppercase tracking-[0.16em] text-ocean-light">Nota</div>
            <p className="mt-1 text-sm text-fg/60">
              Esta vista mostra o estado operacional dos agents backend. A página atualiza automaticamente a cada 15 segundos.
            </p>
          </div>
        </aside>
      </div>

      {/* AI Suggestions com ações */}
      <section className="mt-6 rounded-lg border border-accent/15 bg-fg/[0.04] p-4">
        <h2 className="font-serif text-xl font-black text-accent">Sugestões do AiBrainAgent</h2>
        <p className="mt-1 text-sm text-fg/50">
          {aiRun
            ? `Última execução: ${new Date(aiRun.generatedAt).toLocaleString('pt-PT')} · ${aiRun.mode === 'online' ? `OpenAI ${aiRun.model}` : 'fallback local'}`
            : 'Executa o agente para gerar missões, POIs, ofertas e alertas explicados pela IA.'}
        </p>

        {suggestions ? (
          <div className="mt-4 grid gap-4 xl:grid-cols-4">
            {/* Missões diárias */}
            <SuggestionColumn title="Missões diárias">
              {suggestions.dailyMissions?.map((mission) => (
                <SuggestionCard
                  key={mission.missionId || mission.title}
                  title={mission.title}
                  detail={mission.reason}
                  action={
                    mission.missionId ? (
                      <button
                        onClick={() => activateMission(mission.missionId)}
                        disabled={activatingMission === mission.missionId}
                        className="w-full rounded border border-forest-light/30 bg-forest-light/10 py-1 text-xs font-bold text-forest-light hover:bg-forest-light/20 disabled:opacity-50"
                      >
                        {activatingMission === mission.missionId ? 'A ativar...' : 'Ativar missão'}
                      </button>
                    ) : null
                  }
                />
              ))}
            </SuggestionColumn>

            {/* POIs recomendados */}
            <SuggestionColumn title="POIs recomendados">
              {suggestions.recommendedPOIs?.map((poi, index) => (
                <SuggestionCard
                  key={poi.id}
                  title={`${index + 1}. ${poi.name}`}
                  meta={poi.distance ? `${poi.distance}m` : '-'}
                  detail={poi.reason}
                  action={
                    poi.id ? (
                      <button
                        onClick={() => navigate(`/admin/pois?highlight=${poi.id}`)}
                        className="w-full rounded border border-ocean-light/30 bg-ocean-light/10 py-1 text-xs font-bold text-ocean-light hover:bg-ocean-light/20"
                      >
                        Ver POI
                      </button>
                    ) : null
                  }
                />
              ))}
            </SuggestionColumn>

            {/* Ofertas personalizadas */}
            <SuggestionColumn title="Ofertas personalizadas">
              {suggestions.personalizedOffers?.map((offer) => (
                <SuggestionCard
                  key={offer.id}
                  title={offer.title}
                  meta={`${offer.coinCost} moedas`}
                  detail={offer.reason}
                  action={
                    offer.id ? (
                      <button
                        onClick={() => navigate(`/admin/offers?highlight=${offer.id}`)}
                        className="w-full rounded border border-gem/30 bg-gem/10 py-1 text-xs font-bold text-gem hover:bg-gem/20"
                      >
                        Ver oferta
                      </button>
                    ) : null
                  }
                />
              ))}
            </SuggestionColumn>

            {/* Segurança */}
            <SuggestionColumn title="Segurança">
              <SuggestionCard
                title={`Risco: ${suggestions.securityAlert?.risk || 'low'}`}
                meta={`Ação: ${suggestions.securityAlert?.recommendedAction || 'monitor'}`}
                detail={suggestions.securityAlert?.reason}
                action={
                  suggestions.securityAlert?.risk !== 'low' ? (
                    <button
                      onClick={() => navigate('/admin/fraud-logs')}
                      className="w-full rounded border border-energy/30 bg-energy/10 py-1 text-xs font-bold text-energy hover:bg-energy/20"
                    >
                      Ver logs de fraude
                    </button>
                  ) : null
                }
              />
            </SuggestionColumn>
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-fg/10 bg-black/15 p-6 text-sm text-fg/45">
            Sem sugestões geradas nesta sessão. Clica em <strong className="text-fg/60">Executar agente</strong> para gerar.
          </div>
        )}
      </section>

      {/* Criar com IA */}
      <section className="mt-6 rounded-lg border border-accent/15 bg-fg/[0.04] p-4">
        <h2 className="font-serif text-xl font-black text-accent">Criar com IA</h2>
        <p className="mt-1 text-sm text-fg/50">
          Descreve em linguagem natural e a IA gera os campos automaticamente. Revisas e crias com um clique.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            onClick={() => setAiModal('poi')}
            className="rounded-lg border border-accent/40 bg-accent/10 px-5 py-2.5 text-sm font-bold text-accent hover:bg-accent/20"
          >
            + Sugerir novo POI
          </button>
          <button
            onClick={() => setAiModal('zone')}
            className="rounded-lg border border-ocean-light/40 bg-ocean-light/10 px-5 py-2.5 text-sm font-bold text-ocean-light hover:bg-ocean-light/20"
          >
            + Sugerir nova Zona
          </button>
        </div>
      </section>

      {/* Logs */}
      <section className="mt-6 rounded-lg border border-accent/15 bg-fg/[0.04] p-4">
        <h2 className="font-serif text-xl font-black text-accent">Logs do AiBrainAgent</h2>
        <div className="mt-4 grid gap-3">
          {aiLogs.length === 0 && (
            <div className="rounded-lg border border-fg/10 bg-black/15 p-4 text-sm text-fg/45">Ainda não há execuções registadas.</div>
          )}
          {aiLogs.map((log) => (
            <div key={log.id} className="grid gap-3 rounded-lg border border-fg/10 bg-black/15 p-3 md:grid-cols-[170px_110px_1fr_100px]">
              <div className="font-mono text-xs text-fg/45">{new Date(log.createdAt).toLocaleString('pt-PT')}</div>
              <div className={`text-xs font-bold uppercase ${log.status === 'completed' ? 'text-forest-light' : log.status === 'failed' ? 'text-energy' : 'text-accent'}`}>
                {log.status}
              </div>
              <div className="text-sm text-fg/65">{log.message}</div>
              <div className="text-right text-xs text-fg/35">{log.durationMs ? `${log.durationMs} ms` : '-'}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Modais */}
      <AiCreateModal open={aiModal === 'poi'} onClose={() => setAiModal(null)} type="poi" />
      <AiCreateModal open={aiModal === 'zone'} onClose={() => setAiModal(null)} type="zone" />
    </section>
  );
}
